import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Inbox, LogOut, Shield, User } from "lucide-react";
import { getCurrentRole, getCurrentEmail } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Panoramica", icon: LayoutDashboard, exact: true },
  { to: "/admin/richieste", label: "Richieste", icon: Inbox },
];

export const AdminLayout = ({ children }) => {
  const loc = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");

  if (!token) return <Navigate to="/admin/login" replace />;

  const role = getCurrentRole();
  const email = getCurrentEmail();
  const isSuper = role === "admin";

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    localStorage.removeItem("admin_role");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col">
        <div className="p-6 border-b border-slate-200">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logo.png" alt="Officina Franco" className="h-12 w-auto" />
            <div>
              <div className="font-serif-display text-lg text-slate-900">
                Officina <span className="text-red-600">Franco</span>
              </div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-slate-500">Admin Panel</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 text-xs tracking-[0.2em] uppercase border-l-2 ${
                  active
                    ? "bg-blue-50 text-blue-900 border-red-600"
                    : "text-slate-600 hover:bg-slate-50 border-transparent hover:text-slate-900"
                }`}
              >
                <Icon className="w-4 h-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 space-y-2">
          <div data-testid="role-badge" className={`flex items-center gap-2 px-4 py-2.5 text-xs ${isSuper ? "bg-red-50 text-red-700 border border-red-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
            {isSuper ? <Shield className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
            <div className="flex-1 min-w-0">
              <div className="tracking-[0.15em] uppercase text-[10px] font-medium">{isSuper ? "Amministratore" : "Operatore"}</div>
              <div className="truncate text-[10px] opacity-75">{email}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-3 text-xs tracking-[0.2em] uppercase text-slate-600 hover:text-red-600"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="lg:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <Link to="/admin" className="font-serif-display text-lg">
            Officina <span className="text-red-600">Franco</span>
          </Link>
          <button onClick={logout} className="text-xs text-slate-500">Esci</button>
        </header>
        <nav className="lg:hidden flex bg-white border-b border-slate-200 overflow-x-auto">
          {NAV.map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`px-5 py-3 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap border-b-2 ${
                  active ? "border-red-600 text-blue-900" : "border-transparent text-slate-500"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
        <main className="flex-1 p-6 lg:p-10 overflow-x-auto">{children}</main>
      </div>
    </div>
  );
};
